import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Card, 
  CardContent, 
  CircularProgress, 
  Divider, 
  FormControl, 
  FormHelperText, 
  Grid, 
  InputLabel, 
  MenuItem, 
  Select, 
  TextField, 
  Typography,
  Alert,
  Paper,
  IconButton,
  Tooltip
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon,
  ContentCopy as ContentCopyIcon,
  Key as KeyIcon
} from '@mui/icons-material';
import { Link, useParams, useNavigate } from 'react-router-dom';
import rfidService, { Reader } from '../../../../services/rfidService';
import toast from '../../../../utils/toast';

const KeyGeneration: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [reader, setReader] = useState<Reader | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [generating, setGenerating] = useState<boolean>(false);
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);
  const [expiresInDays, setExpiresInDays] = useState<number | ''>('');
  
  useEffect(() => {
    if (id) {
      fetchReader(id);
    }
  }, [id]);

  const fetchReader = async (readerId: string) => {
    try {
      setLoading(true);
      const data = await rfidService.getReaderById(readerId);
      setReader(data);
    } catch (error) {
      console.error('Error fetching reader:', error);
      toast.error('Failed to fetch reader details');
    } finally {
      setLoading(false);
    }
  };

  const handleExpiryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setExpiresInDays(value === '' ? '' : Number(value));
  };

  const handleGenerateKey = async () => {
    if (!id) return;
    
    try {
      setGenerating(true);
      const data = await rfidService.generateReaderKey(
        id, 
        expiresInDays !== '' ? expiresInDays : undefined
      );
      setGeneratedKey(data.auth_key);
      toast.success('Authentication key generated successfully');
      
      // Refresh reader data
      fetchReader(id);
    } catch (error) {
      console.error('Error generating key:', error);
      toast.error('Failed to generate authentication key');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopyKey = () => {
    if (generatedKey) {
      navigator.clipboard.writeText(generatedKey)
        .then(() => {
          toast.success('Key copied to clipboard');
        })
        .catch((err) => {
          console.error('Failed to copy key:', err);
          toast.error('Failed to copy key to clipboard');
        });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (!reader) {
    return (
      <Box>
        <Typography variant="h5" color="error">
          Reader not found
        </Typography>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          component={Link}
          to="/rfid/readers"
          sx={{ mt: 2 }}
        >
          Back to Readers
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center">
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            component={Link}
            to={`/rfid/readers/${id}`}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h4" component="h1">
            Generate Authentication Key
          </Typography>
        </Box>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Reader Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                Name
              </Typography>
              <Typography variant="body1" gutterBottom>
                {reader.name}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                Device ID
              </Typography>
              <Typography variant="body1" gutterBottom>
                {reader.device_id}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                Current Key Status
              </Typography>
              <Typography variant="body1" gutterBottom>
                {reader.is_revoked ? (
                  <Alert severity="error">Key Revoked</Alert>
                ) : reader.auth_key_created_at ? (
                  reader.auth_key_expires_at ? (
                    <Alert severity="info">
                      Key Active - Expires on {new Date(reader.auth_key_expires_at).toLocaleDateString('en-US')}
                    </Alert>
                  ) : (
                    <Alert severity="success">Key Active (No Expiry)</Alert>
                  )
                ) : (
                  <Alert severity="warning">No Key Generated</Alert>
                )}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Generate New Authentication Key
          </Typography>
          
          {reader.auth_key_created_at && !reader.is_revoked && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              This reader already has an active authentication key. Generating a new key will invalidate the existing key.
            </Alert>
          )}
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Expires In (Days)"
                type="number"
                value={expiresInDays}
                onChange={handleExpiryChange}
                helperText="Leave empty for a key that never expires"
                inputProps={{ min: 1 }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<KeyIcon />}
                onClick={handleGenerateKey}
                disabled={generating}
              >
                {generating ? (
                  <>
                    <CircularProgress size={24} sx={{ mr: 1 }} />
                    Generating...
                  </>
                ) : (
                  'Generate Key'
                )}
              </Button>
            </Grid>
          </Grid>
          
          {generatedKey && (
            <Box mt={4}>
              <Alert severity="success" sx={{ mb: 2 }}>
                Authentication key generated successfully. Please copy this key now as it will not be shown again.
              </Alert>
              
              <Paper
                sx={{
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  backgroundColor: '#f5f5f5',
                  fontFamily: 'monospace',
                  wordBreak: 'break-all'
                }}
              >
                <Typography variant="body2" sx={{ flexGrow: 1 }}>
                  {generatedKey}
                </Typography>
                <Tooltip title="Copy to clipboard">
                  <IconButton onClick={handleCopyKey} size="small">
                    <ContentCopyIcon />
                  </IconButton>
                </Tooltip>
              </Paper>
              
              <Box mt={3} display="flex" justifyContent="flex-end">
                <Button
                  variant="contained"
                  component={Link}
                  to={`/rfid/readers/${id}`}
                >
                  Done
                </Button>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default KeyGeneration;